import axios from 'axios';
import { getApiUrl } from '../config/api.config';
import { getAuthHeaders } from '../config/api.config';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: process.env.NODE_ENV === 'production' 
    ? process.env.REACT_APP_PRODUCTION_API_URL 
    : process.env.REACT_APP_API_URL, // Use environment variables
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Don't send credentials by default
});

// Set up response interceptor for handling token refresh
apiClient.interceptors.response.use(
  response => response,
  async error => {
    // Handle specific error cases
    return Promise.reject(error);
  }
);

// Standard API request functions
export const apiRequest = async (endpoint, method = 'GET', data = null, customHeaders = {}) => {
  try {
    const headers = {
      ...getAuthHeaders(),
      ...customHeaders,
    };
    
    // Use the endpoint directly without getApiUrl
    const config = {
      url: endpoint,
      method,
      headers,
    };
    
    if (data) {
      if (method.toUpperCase() === 'GET') {
        config.params = data;
      } else {
        config.data = data;
      }
    }
    
    const response = await apiClient(config);
    return response.data;
  } catch (error) {
    console.error('API Request Error:', error);
    
    // Enhance error object with user-friendly messages
    if (error.response) {
      // Server responded with an error status
      if (error.response.status === 401) {
        // Add a better message for unauthorized errors
        error.friendlyMessage = 'Invalid credentials. Please check your email and password.';
      } else if (error.response.status === 403) {
        error.friendlyMessage = 'You do not have permission to perform this action.';
      } else if (error.response.status === 404) {
        error.friendlyMessage = 'The requested resource was not found.';
      } else if (error.response.status === 500) {
        error.friendlyMessage = 'Server error. Please try again later.';
      }
    } else if (error.request) {
      // Request was made but no response received
      error.friendlyMessage = 'Unable to connect to the server. Please check your internet connection.';
    } else {
      // Something else caused the error
      error.friendlyMessage = 'An unexpected error occurred. Please try again.';
    }
    
    throw error;
  }
};

// Convenience methods
export const getData = (endpoint, params = {}, headers = {}) => 
  apiRequest(endpoint, 'GET', params, headers);

export const postData = (endpoint, data = {}, headers = {}) => 
  apiRequest(endpoint, 'POST', data, headers);

export const putData = (endpoint, data = {}, headers = {}) => 
  apiRequest(endpoint, 'PUT', data, headers);

export const patchData = (endpoint, data = {}, headers = {}) => 
  apiRequest(endpoint, 'PATCH', data, headers);

export const deleteData = (endpoint, data = {}, headers = {}) => 
  apiRequest(endpoint, 'DELETE', data, headers);

export default {
  get: getData,
  post: postData,
  put: putData,
  patch: patchData,
  delete: deleteData,
};
